<template>
  <BContentBox>
    <template #content>
      <div v-loading="settingLoading">
        <BLayout
          justify-center
          column
          class="section"
        >
          <div class="title">
            {{ $t(`corpSettingApi.zoom.usageSetting.title`) }}
          </div>
          <div
            class="explain"
          >
            {{ $t('corpSettingApi.zoom.usageSetting.explain') }}
          </div>
          <div class="switch-container">
            <div>{{ $t('corpSettingApi.zoom.usageSetting.switchLable') }}</div>
            <BSwitch
              :disabled="disableUsageStatusBtn"
              :status="!!zoomSetting?.useZoomPhone"
              :before-change="handleUsageStatusChange"
              :loading="updating"
              color="accent"
            />
          </div>
        </BLayout>
        <BLayout
          justify-center
          column
          class="section"
        >
          <BListItem class="title">
            {{ $t(`corpSettingApi.zoom.apiIntegrationSetting.title`) }}
          </BListItem>
          <BListItem
            class="explain"
          >
            {{ $t('corpSettingApi.zoom.apiIntegrationSetting.explain') }}
          </BListItem>
          <div class="mt-100">
            <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.connectStatus') }}：</span>
            <span v-if="!isConnect">{{ $t('corpSettingApi.zoom.apiIntegrationSetting.status.unconnect') }}</span>
            <span v-if="isConnect">{{ $t('corpSettingApi.zoom.apiIntegrationSetting.status.connect') }}</span>
            <span
              v-if="isExpired"
              class="token-expire"
            >
              {{ $t('corpSettingApi.zoom.apiIntegrationSetting.status.invalid') }}
            </span>
          </div>
          <BListItem
            v-if="isConnect"
            class="mt-50"
          >
            <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.connectUserName') }}：</span>
            <span>{{ zoomSetting?.apiSetting?.connectUserName || '' }}</span>
          </BListItem>
          <BListItem class="mt-300">
            <BBtn
              v-if="isConnect"
              type="primary"
              :disabled="disableConnectBtn"
              @click="unconnect"
            >
              <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.btnDisconnect') }}</span>
            </BBtn>
            <BBtn
              v-else
              type="primary"
              :disabled="settingLoading || disableConnectBtn || !authorize(['owner', 'bpo_guest'])"
              @click="connect"
            >
              <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.btnText') }}</span>
            </BBtn>
          </BListItem>
        </BLayout>
      </div>
    </template>
  </BContentBox>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ZoomSettingApiService } from '@/api/user/resources/zoom_setting';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useWait } from '@/composable/vue-wait';
import ZoomSettingStatuses from '@/const/zoom_setting_statuses';
import { unconnectAllExcept } from '@/services/cti_service';

export default defineComponent({
  setup() {
    const { inject: injectCurrentUser } = useCurrentUserProvider();
    const { currentUser, authorize } = injectCurrentUser();
    const { doActionWithWait, wait } = useWait();
    return {
      doActionWithWait,
      wait,
      currentUser,
      authorize,
    };
  },
  data() {
    return {
      zoomSetting: null,
    };
  },
  computed: {
    settingLoading(): boolean {
      return this.wait.is(['getZoomSettingWait', 'disconnectZoomApiWait', 'updateZoomSettingWait']);
    },
    deleting(): boolean {
      return this.wait.is(['deleteZoomSettingWait']);
    },
    updating(): boolean {
      return this.wait.is(['updateZoomSettingWait']);
    },
    isConnect(): boolean {
      return this.zoomSetting?.apiSetting?.connectStatus ==  ZoomSettingStatuses.CONNECTED;
    },
    isExpired(): boolean {
      return this.zoomSetting?.apiSetting?.connectStatus == ZoomSettingStatuses.TOKEN_EXPIRED;
    },
    authLink(): string {
      const url = 'https://zoom.us/oauth/authorize';
      const clientId = import.meta.env.VITE_ZOOM_CLIENT_ID;
      const splitHostname = location.hostname.split('.');
      splitHostname.shift();
      const appDomain = splitHostname.join('.');
      const redirectUrl = `https://${this.currentUser.slug}.${appDomain}/app/zoom/oauth/callback`;
      return `${url}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUrl}`;
    },
    disableEditing(): boolean {
      return !this.authorize(['owner', 'bpo_guest']);
    },
    disableUsageStatusBtn(): boolean {
      return this.disableEditing;
    },
    disableConnectBtn(): boolean {
      return this.disableEditing || this.settingLoading || !this.zoomSetting?.useZoomPhone;
    },
  },
  created() {
    this.fetchZoomSetting();
  },
  methods: {
    async fetchZoomSetting() {
      const api = new ZoomSettingApiService();
      await this.doActionWithWait('getZoomSettingWait', async () => {
        const { data: { zoomSetting } } = await api.getZoomSetting();
        this.zoomSetting = zoomSetting;
      });
    },
    connect(): void {
      location.href = this.authLink;
    },
    async unconnect(): Promise<void> {
      const check = await this.$bitterAlert.show({
        title: this.$t('corpSettingApi.zoom.apiIntegrationSetting.cancelLinkConfirmText'),
        text: this.$t('general.confirmText'),
      });
      if (!check) { return; }
      const api = new ZoomSettingApiService();
      await this.doActionWithWait('disconnectZoomApiWait', async () => {
        await api.disconnectZoomApi();
        await this.fetchZoomSetting();
      });
    },
    async handleUsageStatusChange() {
      const newValue = !this.zoomSetting?.useZoomPhone;
      if (!newValue && this.isConnect) {
        const isWarningAccepted = await this.$bitterAlert.show({
          title: this.$t('corpSettingApi.zoom.apiIntegrationSetting.unuseConfirmText'),
          text: this.$t('general.confirmText'),
        });
        if (!isWarningAccepted) { return; }
      }
      if (newValue) {
        await unconnectAllExcept('zoom');
      }
      const api = new ZoomSettingApiService();
      await this.doActionWithWait('updateZoomSettingWait', async () => {
        await api.updateZoomSetting({
          request: {
            updateZoomSettingBody: {
              useZoomPhone: newValue,
            },
          },
        });
        // NOTE: ZoomApi連携状態も更新される可能性があるため、再取得する。
        // NOTE: "doActionWithWait"の外に出すと、ローディングインジケータのチラつきが気になるので、中に入れている。
        await this.fetchZoomSetting();
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.explain {
  color: $concrete-dark;
  margin-bottom: 20px;
  white-space: pre-wrap;
}

.token-expire {
  color: $basecolor-error;
}

.general-footer {
  padding-top: $basespace-600;
}

.section {
  margin-bottom: 100px;
  max-width: 500px;
}

.title {
  font-weight: bold;
  margin-bottom: 10px;
}

.switch-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
